import React, { useCallback, useMemo } from 'react';
import { Button, Modal, ModalTitle } from 'react-bootstrap';
import PropTypes from 'prop-types';
import {Steps} from './Steps';
import {TourSteps} from './tourSteps';

const StepModal = React.memo((props) => {
    // Destructure props for better performance and linting compliance
    const {
        showModal,
        currentTourStep,
        setShowExitTour,
        setShowLearnRovr,
        previousTourStep,
        nextTourStep
    } = props;
    
    // Memoized event handlers with useCallback for stable references
    const hideTour = useCallback(() => {
        setShowExitTour(true);
    }, [setShowExitTour]);

    const seeMore = useCallback(() => {
        setShowLearnRovr(0);
    }, [setShowLearnRovr]);

    const handlePreviousTourStep = useCallback(() => {
        previousTourStep();
    }, [previousTourStep]);

    const handleNextTourStep = useCallback(() => {
        nextTourStep();
    }, [nextTourStep]);

    // Memoized computed values
    const currentStepData = useMemo(() => {
        return TourSteps[currentTourStep];
    }, [currentTourStep]);

    const stepText = useMemo(() => {
        if (!currentStepData || !Steps[currentStepData.step]) {
            return null;
        }
        return Steps[currentStepData.step].text();
    }, [currentStepData]);

    const isLastStep = useMemo(() => {
        return currentTourStep === 14;
    }, [currentTourStep]);

    // Memoized modal styles and props
    const modalProps = useMemo(() => ({
        dialogClassName: "fullscreenmodal",
        show: showModal,
        onHide: hideTour,
        backdrop: "static",
        keyboard: false,
        id: "about"
    }), [showModal, hideTour]);

    const headerTitleStyle = useMemo(() => ({
        className: "aboutHead"
    }), []);

    const modalBodyTitleStyle = useMemo(() => ({
        className: "titleBody stepBody"
    }), []);

    const buttonContainerStyle = useMemo(() => ({
        className: 'stepTourButtons'
    }), []);

    const backButtonStyle = useMemo(() => ({
        className: "mr-2 buttonSearch submitColor buttonTour"
    }), []);

    const actionButtonStyle = useMemo(() => ({
        className: "mr-2 buttonSearch resetColor buttonTour"
    }), []);

    // Memoized action button rendering
    const actionButton = useMemo(() => {
        if (isLastStep) {
            return (
                <Button 
                    {...actionButtonStyle}
                    onClick={seeMore}
                >
                    See More
                </Button>
            );
        }
        
        return (
            <Button 
                {...actionButtonStyle}
                onClick={handleNextTourStep}
            >
                Next
            </Button>
        );
    }, [isLastStep, actionButtonStyle, seeMore, handleNextTourStep]);

    return (
        <>
            <Modal {...modalProps}>
                <Modal.Header closeButton>
                    <Modal.Title {...headerTitleStyle}>Tasks</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ModalTitle {...modalBodyTitleStyle}>
                        {stepText}
                    </ModalTitle>
                </Modal.Body>
                <div {...buttonContainerStyle}>
                    <Button 
                        {...backButtonStyle}
                        onClick={handlePreviousTourStep}
                    >
                        Back
                    </Button>
                    {actionButton}
                </div>
            </Modal>
        </>
    );
});

StepModal.displayName = 'StepModal';

StepModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    currentTourStep: PropTypes.number.isRequired,
    setShowExitTour: PropTypes.func.isRequired,
    setShowLearnRovr: PropTypes.func.isRequired,
    previousTourStep: PropTypes.func.isRequired,
    nextTourStep: PropTypes.func.isRequired
};

export default StepModal;